/**
 * Track lowering: keys -> RPE events (W1-04 core-ir; DESIGN.md §6.3, §6.11, §6.12; contract core/ir/types.ts).
 *
 * For keys k_0..k_m (model invariant: a hold arrives as an explicit jump, k_{i+1}.vIn === k_i.vOut, so
 * `end = k_{i+1}.vIn` for every segment kind):
 *   segment i, hold{gap}, previous emitted event ends at k_i.t with value k_i.vOut -> no event (Phira holds it);
 *                         otherwise a constant event (counted in LowerReport.gapsMaterialised)
 *   segment i, hold      -> constant event [k_i.t, k_{i+1}.t] holding k_i.vOut
 *   segment i, tween/bez -> one event [k_i.t, k_{i+1}.t], start = k_i.vOut, end = k_{i+1}.vIn
 *                           (legacy-mode eased speed, and a bezier on speed in any mode: linear pieces for the
 *                           mode's integrator, speedBake.ts)
 *   single key           -> constant event [k_0.t, k_0.t + 1 beat] (gif: + 1/32 beat)
 *   last key             -> a trailing constant event [k_m.t, k_m.t + 1 beat] holding k_m.vOut whenever the value
 *                           Phira holds after the last emitted event differs from k_m.vOut (a jump on the last key,
 *                           or a baked last segment); never on gif tracks (it would freeze the GIF)
 * Output: non-empty, sorted, non-overlapping, positive-length events that evaluate like the key model.
 *
 * Provenance write-back: raw triples while equal in value (triples.ts), raw easing fields while equivalent
 * (eventFields.ts), src.rawEndValue on rpe170-mode speed steps, linkgroup, unknown keys (src.extra).
 * Speed events never carry a bezier in any mode.
 */

import { resolveRpe, ZERO_BEZIER } from '../ease/rpeMap';
import type { Ease } from '../ease/types';
import { trackValueEquals } from '../model/invariants';
import type { Key, LineProp, Track, TrackId, TrackValue } from '../model/types';
import { Beats } from '../time/beat';
import type { Beat, Triple } from '../time/types';
import type { NonEmpty, RGB } from '../util/types';
import {
  bezierFields,
  constantFields,
  ignoredEaseFields,
  LINEAR_FIELDS,
  tweenFields,
  type EventEaseFields,
} from './eventFields';
import type { LowerAcc, LowerCtx } from './lowerCtx';
import { rgbOut } from './lowerNotes';
import { bakeSpeedSegment, PHIRA_END_SAMPLE_SEC } from './speedBake';
import { beatPlus, tripleOut } from './triples';
import type { IrEvent, IrEventList, IrEventOrigin } from './types';

/** Value of a line event list. */
export type LineEventValue = number | RGB | string;

/** Time span of one emitted constant or segment event. */
export interface Span {
  readonly startBeat: Beat;
  readonly endBeat: Beat;
  readonly startTriple: Triple;
  readonly endTriple: Triple;
}

/** Hooks turning the key walk into concrete events (line events or extra.json keyframes). */
export interface WalkHooks<E> {
  /** GIF progress track: 1/32-beat single event, never a trailing event. */
  readonly gif: boolean;
  /** Event(s) of a tween / bezier segment k -> n; returns the value the last one ends with. */
  segment(k: Key, n: Key, span: Span): { readonly events: readonly E[]; readonly end: TrackValue };
  /** A constant event starting at k (hold segment, materialised gap, single key, trailing jump). */
  constant(
    k: Key,
    span: Span,
    origin: Extract<IrEventOrigin, 'segment' | 'single' | 'trailing'>,
  ): { readonly event: E; readonly end: TrackValue };
}

function isGap(e: Ease): boolean {
  return e.kind === 'hold' && e.gap === true;
}

/** The key walk of DESIGN.md §6.3 (shared by line tracks and extra.json variable tracks). */
export function walkKeys<E>(keys: readonly Key[], hooks: WalkHooks<E>, acc: LowerAcc): E[] {
  const out: E[] = [];
  const last = keys[keys.length - 1];
  if (last === undefined) return out;
  let lastEndBeat: Beat | null = null;
  let lastEndValue: TrackValue | null = null;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i] as Key;
    const n = keys[i + 1] as Key;
    if (isGap(k.ease)) {
      if (lastEndBeat !== null && Beats.eq(lastEndBeat, k.t) && trackValueEquals(lastEndValue, k.vOut)) continue;
      acc.gapsMaterialised++;
    }
    const span: Span = {
      startBeat: k.t,
      endBeat: n.t,
      startTriple: tripleOut(k.t, k.src?.rawStart, acc),
      endTriple: tripleOut(n.t, n.src?.rawEnd, acc),
    };
    if (k.ease.kind === 'hold') {
      const r = hooks.constant(k, span, 'segment');
      out.push(r.event);
      lastEndValue = r.end;
    } else {
      const r = hooks.segment(k, n, span);
      out.push(...r.events);
      lastEndValue = r.end;
    }
    lastEndBeat = n.t;
  }
  if (keys.length === 1) {
    const endBeat = beatPlus(last.t, 1, hooks.gif ? 32 : 1);
    const span: Span = {
      startBeat: last.t,
      endBeat,
      startTriple: tripleOut(last.t, last.src?.rawStart, acc),
      endTriple: Beats.toTriple(endBeat),
    };
    out.push(hooks.constant(last, span, 'single').event);
  } else if (!hooks.gif && lastEndValue !== null && !trackValueEquals(lastEndValue, last.vOut)) {
    const endBeat = beatPlus(last.t, 1, 1);
    const span: Span = {
      startBeat: last.t,
      endBeat,
      startTriple: tripleOut(last.t, last.src?.rawStart, acc),
      endTriple: Beats.toTriple(endBeat),
    };
    out.push(hooks.constant(last, span, 'trailing').event);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Line tracks
// ---------------------------------------------------------------------------------------------------------------

function lineEvent(
  k: Key,
  span: Span,
  start: TrackValue,
  end: TrackValue,
  fields: EventEaseFields,
  origin: IrEventOrigin,
  withExtra: boolean,
): IrEvent<LineEventValue> {
  return {
    startTime: span.startTriple,
    endTime: span.endTriple,
    start: start as LineEventValue,
    end: end as LineEventValue,
    easingType: fields.easingType,
    easingLeft: fields.easingLeft,
    easingRight: fields.easingRight,
    bezier: fields.bezier,
    bezierPoints: fields.bezierPoints,
    linkgroup: k.link,
    extra: withExtra ? (k.src?.extra ?? []) : [],
    origin,
    srcKey: k.id,
  };
}

/** True when a speed tween segment is not plain linear in Phira (legacy mode bakes it). */
function easedSpeed(e: Ease): boolean {
  if (e.kind === 'bezier') return true;
  if (e.kind !== 'tween') return false;
  const desc = resolveRpe(e.id, e.left, e.right, 0, ZERO_BEZIER);
  return !(desc.kind === 'static' && desc.tween === 2);
}

function lineHooks(ctx: LowerCtx, prop: LineProp): WalkHooks<IrEvent<LineEventValue>> {
  const speed = prop === 'speed';
  const legacy = ctx.opts.speedMode === 'legacy';
  // Colour channels are written as integers clamped to 0..255 (WORKPACKAGES rule 8).
  const out = (v: TrackValue): TrackValue => (prop === 'color' ? rgbOut(v as RGB) : v);
  return {
    gif: prop === 'gif',
    constant(k, span, origin) {
      let fields: EventEaseFields = constantFields(k.src);
      let end: TrackValue = k.vOut;
      if (speed && legacy) fields = ignoredEaseFields(k.ease, k.src) ?? fields;
      if (speed && !legacy && origin === 'segment' && k.ease.kind === 'hold' && k.ease.gap !== true) {
        // rpe170 step (easingType 0): constant start speed in Phira's 170 integrator, raw end written back.
        const raw = k.src?.rawEndValue;
        if (raw !== undefined && (k.src?.easingType ?? 1) === 0) end = raw;
      }
      return { event: lineEvent(k, span, out(k.vOut), out(end), fields, origin, true), end };
    },
    segment(k, n, span) {
      const e = k.ease;
      if (speed && typeof k.vOut === 'number' && typeof n.vIn === 'number') {
        const mustBake = e.kind === 'bezier' || (legacy && easedSpeed(e));
        if (mustBake && !(e.kind !== 'bezier' && k.vOut === n.vIn)) return bakeSegment(ctx, k, n, span);
      }
      let fields: EventEaseFields;
      if (e.kind === 'bezier') fields = bezierFields(e, k.src);
      else if (e.kind === 'tween') {
        let src = k.src;
        if (speed && !legacy && src?.easingType === 0) {
          // easingType 0 is a step (constant start speed) in Phira's 170 integrator: never on a ramp.
          const { easingType: _step, ...rest } = src;
          src = rest;
        }
        fields = (speed && legacy ? ignoredEaseFields(e, src) : null) ?? tweenFields(e, src);
      } else fields = constantFields(k.src);
      return { events: [lineEvent(k, span, out(k.vOut), out(n.vIn), fields, 'segment', true)], end: n.vIn };
    },
  };
}

/** Linear pieces of an eased speed segment (legacy mode; bezier on speed in any mode). */
function bakeSegment(
  ctx: LowerCtx,
  k: Key,
  n: Key,
  span: Span,
): { readonly events: readonly IrEvent<LineEventValue>[]; readonly end: TrackValue } {
  const tempo = ctx.tempo();
  const result = bakeSpeedSegment({
    t0: span.startBeat,
    t1: span.endBeat,
    sec0: tempo.secAtTriple(span.startTriple),
    sec1: tempo.secAtTriple(span.endTriple),
    v0: k.vOut as number,
    v1: n.vIn as number,
    ease: k.ease,
    tolPx: ctx.opts.speedBakeTolPx,
    secAt: (b) => tempo.secAt(b),
    // Pieces are compensated for the legacy integrator's 0.1 ms end sample; rpe170 integrates them exactly.
    endSampleSec: ctx.opts.speedMode === 'legacy' ? PHIRA_END_SAMPLE_SEC : 0,
  });
  ctx.acc.bakedSpeed.push({ key: k.id, pieces: result.pieces.length, maxErrorPx: result.maxErrorPx });
  const last = result.pieces.length - 1;
  const events = result.pieces.map((p, j) =>
    lineEvent(
      k,
      {
        startBeat: p.startBeat,
        endBeat: p.endBeat,
        startTriple: j === 0 ? span.startTriple : Beats.toTriple(p.startBeat),
        endTriple: j === last ? span.endTriple : Beats.toTriple(p.endBeat),
      },
      p.start,
      p.end,
      LINEAR_FIELDS,
      'bakePiece',
      j === 0,
    ),
  );
  return { events, end: (result.pieces[last] as { readonly end: number }).end };
}

/** Keys of a track in beat order. */
export function trackKeys(ctx: LowerCtx, track: TrackId): Key[] {
  const out: Key[] = [];
  for (const id of ctx.idx.keysByTrack(track)) {
    const k = ctx.doc.keys.get(id);
    if (k !== undefined) out.push(k);
  }
  return out;
}

/** Events of a line track (null for a track without keys). */
export function lowerLineTrack(ctx: LowerCtx, track: Track): IrEventList<LineEventValue> | null {
  if (track.owner.kind !== 'line') return null;
  const keys = trackKeys(ctx, track.id);
  const events = walkKeys(keys, lineHooks(ctx, track.owner.prop), ctx.acc);
  return events.length === 0 ? null : (events as unknown as NonEmpty<IrEvent<LineEventValue>>);
}

/**
 * `Lower.lowerTrack`: the events of one line track (null when the track does not exist, has no keys, or is an
 * effect / video variable track: those are lowered by `lowerExtra` with extra.json keyframe rules).
 */
export function lowerTrackIr(ctx: LowerCtx, track: TrackId): IrEventList<LineEventValue> | null {
  const t = ctx.doc.tracks.get(track);
  if (t === undefined) return null;
  return lowerLineTrack(ctx, t);
}
